import { SectionType } from 'types/studio/studioUtility';

type CustomizableProperty = string | { path: string | string[]; key?: string };

export type CustomizableMediaProperty = CustomizableProperty & { mediaTypePath?: string } & Record<
    string,
    any
  >;

type CustomizableField = {
  paddingBottom?: CustomizableProperty;
  paddingTop?: CustomizableProperty;
  headline?: CustomizableProperty;
  body?: CustomizableProperty;
  bodyText?: CustomizableProperty;
  logo?: CustomizableProperty;
  media?: CustomizableMediaProperty;
  subtitle?: CustomizableProperty;
  'custom-body'?: CustomizableProperty;
  'custom-button'?: CustomizableProperty;
  isFootNoteEnabled?: CustomizableProperty;
  footNoteValue?: CustomizableProperty;
};

/**
 * Content types mapped to their customizable fields, hardcoded (for now) needs
 * to be updated when new _types are added to DynamicSlices in Sanity.
 */
export const customizableFields: Record<string, CustomizableField> = {
  headerLanderV3: {},
  heroHalfV5: {
    headline: 'headline',
    body: 'bodyText.text[0].children[0].text',
    subtitle: 'subtitle',
    media: {
      key: 'hero-image-video',
      path: 'media',
      mediaTypePath: 'media.mediaType'
    },
    logo: {
      key: 'logo-image',
      path: 'logo'
    },
    paddingBottom: 'padding.bottom',
    paddingTop: 'padding.top',
    'custom-button': 'unifiedButton',
    isFootNoteEnabled: 'showfootnote',
    footNoteValue: 'footnote'
  },
  heroText: {
    headline: 'headline',
    'custom-body': 'subtitle[0].children[0].text',
    media: {
      key: 'hero-image-video',
      path: 'media',
      mediaTypePath: 'media.mediaType'
    },
    logo: {
      key: 'logo-image',
      path: 'logo'
    },
    paddingBottom: 'padding.bottom',
    paddingTop: 'padding.top'
  },
  offerV3: {},
  faqV2: {
    paddingBottom: 'padding.bottom',
    paddingTop: 'padding.top'
  },
  benefitsList: {
    headline: 'headline',
    body: 'headlineBody[0].children[0].text',
    media: {
      key: 'benefits-image',
      path: 'image.image'
    },
    paddingBottom: 'padding.bottom',
    paddingTop: 'padding.top'
  },
  valueReviewCards: {
    headline: 'headline',
    body: 'headlineBody[0].children[0].text',
    media: {
      key: 'benefits-image',
      path: [
        'cards[0].hotspotImage',
        'cards[1].hotspotImage',
        'cards[2].hotspotImage',
        'cards[3].hotspotImage'
      ]
    },
    paddingBottom: 'padding.bottom',
    paddingTop: 'padding.top'
  },
  benefitsV2: {
    headline: 'headline',
    body: 'headlineBody[0].children[0].text',
    media: {
      key: 'benefits-image',
      path: 'image.image'
    },
    paddingBottom: 'padding.bottom',
    paddingTop: 'padding.top'
  },
  gallery: {
    headline: 'headline[0].children[0].text',
    media: {
      key: 'how-to-use-image',
      path: [
        'galleryCards[0].image.image',
        'galleryCards[1].image.image',
        'galleryCards[2].image.image',
        'galleryCards[3].image.image'
      ]
    },
    paddingBottom: 'padding.bottom',
    paddingTop: 'padding.top'
  },
  reviews: {
    paddingBottom: 'padding.bottom',
    paddingTop: 'padding.top'
  },
  testimonialsV5: {
    paddingBottom: 'padding.bottom',
    paddingTop: 'padding.top'
  },
  tagsLineV2: {
    headline: '',
    paddingBottom: 'padding.bottom',
    paddingTop: 'padding.top'
  },
  imageAndTiles: {
    headline: 'headline[0].children[0].text',
    media: {
      key: 'research-image',
      path: 'media.image',
      'quality-image-and-tile-general': {
        key: 'quality-image'
      }
    },
    paddingBottom: 'padding.bottom',
    paddingTop: 'padding.top'
  },
  chart: {
    headline: 'headline[0].children[0].text',
    body: 'subtitle[0].children[0].text',
    media: {
      key: 'replacement-value-image',
      path: 'image'
    },
    paddingBottom: 'padding.bottom',
    paddingTop: 'padding.top'
  },
  imageAndExpandableListV2: {
    headline: 'headline',
    body: 'subtile',
    media: {
      key: 'quality-image',
      path: 'image'
    },
    paddingBottom: 'padding.bottom',
    paddingTop: 'padding.top'
  },
  ingredientsList: {
    paddingBottom: 'padding.bottom',
    paddingTop: 'padding.top'
  },
  benefitsTest: {
    media: {
      key: 'reasons-to-believe-image',
      path: 'image.image'
    },
    paddingBottom: 'padding.bottom',
    paddingTop: 'padding.top'
  }
} satisfies Partial<Record<SectionType, CustomizableField>>;
